import { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Search,
  RefreshCcw,
  Download,
  ExternalLink,
  Loader2,
  Copy,
  CreditCard,
  IndianRupee,
  Check,
  X,
  Trash2, // ⬅️ added
} from "lucide-react";
import { toast, ToastContainer } from "react-toastify";
import MyContext from "../../../MyContext";
import "./CreditStyle.css";
import Swal from "sweetalert2";

/* Helper: decode "[Emoji:BASE64]" tokens in customer_name to real emoji */
function decodeEmojiTokens(str = "") {
  return String(str).replace(/\[Emoji:([A-Za-z0-9+/=]+)\]/g, (_, b64) => {
    try {
      const bin =
        typeof atob === "function"
          ? atob(b64)
          : Buffer.from(b64, "base64").toString("binary");
      const utf8 = decodeURIComponent(
        bin
          .split("")
          .map(
            (c) =>
              "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2)
          )
          .join("")
      );
      return utf8;
    } catch {
      return "";
    }
  });
}

export default function OrderInvoices({ userID }) {
  const { baseUrl, config } = useContext(MyContext);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  // --- Payment modal state ---
  const [payOpen, setPayOpen] = useState(false);
  const [payRow, setPayRow] = useState(null);
  const [paymentMode, setPaymentMode] = useState("UPI"); // default UPI
  const [paymentPaid, setPaymentPaid] = useState(false); // checkbox -> paid/unpaid
  const [paySubmitting, setPaySubmitting] = useState(false);

  const API_LIST = "/api/Order/v1.0/getOrderInvoices";
  const API_UPDATE_PAYMENT = "/api/Order/v1.0/updateInvoiceStatus";
  const API_DELETE_INVOICE = "/api/Order/v1.0/deleteInvoice"; // ⬅️ added

  const fetchInvoices = async () => {
    if (!userID) return toast.error("Missing userid");
    try {
      setLoading(true);
      const res = await axios.post(
        `${baseUrl}${API_LIST}`,
        { userid: userID },
        config
      );
      if (res?.data?.responseCode === "00") {
        const list = Array.isArray(res?.data?.response)
          ? res.data.response
          : [];
        const mapped = list.map((r) => ({
          id: String(r.pk_invoice_id ?? ""),
          mobile: String(r.customer_mobile ?? ""),
          amount: String(r.amount ?? ""),
          link: String(r.invoice_link ?? ""),
          number: String(r.invoice_number ?? ""),
          name: decodeEmojiTokens(String(r.customer_name ?? "")),
          status: String(r.status ?? "").toLowerCase(), // "paid"/"unpaid"
          created_on: String(r.created_on ?? ""),
        }));
        setRows(mapped);
      } else {
        setRows([]);
        toast.error(
          res?.data?.responseMessage || "Failed to fetch invoices"
        );
      }
    } catch {
      toast.error("API error while fetching invoices");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userID, baseUrl]);

  // 🔍 Global search only, no column sorting
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;

    return rows.filter((r) =>
      [r.number, r.mobile, r.name, r.status, r.amount, r.created_on]
        .map((x) => String(x).toLowerCase())
        .some((s) => s.includes(needle))
    );
  }, [rows, q]);

  const toCurrency = (x) => {
    const n = Number(x);
    return Number.isFinite(n)
      ? `₹ ${n.toFixed(2)}`
      : `₹ ${x || "—"}`;
  };

  const badgeClass = (status) => {
    const s = String(status || "").toLowerCase();
    if (s === "paid" || s === "success") return "success";
    if (s === "pending" || s === "processing") return "warning";
    if (s === "failed" || s === "unpaid" || s === "cancelled")
      return "danger";
    return "muted";
  };

  const viewInvoice = (url) => {
    if (!url) return toast.warning("Invalid invoice link");
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const copyLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.info("Invoice link copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  const downloadInvoice = async (row) => {
    try {
      if (!row?.link) return toast.warning("No invoice link");
      const resp = await fetch(row.link, { credentials: "omit" });
      if (!resp.ok) throw new Error("Download failed");
      const blob = await resp.blob();
      const ext =
        row.link.split("?")[0].split(".").pop() || "png";
      const fileName = `${row.number || "INVOICE"}.${ext}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Unable to download invoice");
    }
  };

  // --- Delete invoice handler ---
const deleteInvoice = async (row) => {
  if (!row?.id) return toast.error("Missing invoice id");

  const confirmation = await Swal.fire({
    title: "Delete Invoice?",
    text: `Are you sure you want to delete invoice ${row.number}?`,
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#d33",
    cancelButtonColor: "#3085d6",
    confirmButtonText: "Yes, delete it",
  });

  if (!confirmation.isConfirmed) return;

  try {
    const payload = { invoiceId: String(row.id) };

    const res = await axios.post(
      `${baseUrl}${API_DELETE_INVOICE}`,
      payload,
      config
    );

    if (res?.data?.responseCode === "00") {
      Swal.fire({
        title: "Deleted!",
        text: "Invoice deleted successfully.",
        icon: "success",
        timer: 1200,
        showConfirmButton: false,
      });

      // Remove from table
      setRows((prev) => prev.filter((r) => r.id !== row.id));
    } else {
      Swal.fire({
        title: "Error",
        text: res?.data?.responseMessage || "Failed to delete invoice",
        icon: "error",
      });
    }
  } catch (err) {
    Swal.fire({
      title: "Error",
      text: "API error while deleting invoice",
      icon: "error",
    });
  }
};
  // --- Payment modal handlers ---
  const openPayment = (row) => {
    setPayRow(row);
    setPaymentMode("UPI");
    setPaymentPaid(
      String(row?.status || "").toLowerCase() === "paid"
    );
    setPayOpen(true);
  };
  const closePayment = () => {
    if (!paySubmitting) setPayOpen(false);
  };

  const submitPaymentUpdate = async () => {
    if (!payRow?.id) return toast.error("Missing invoice id");
    try {
      setPaySubmitting(true);
      const payload = {
        invoiceId: String(payRow.id),
        paymentMode: String(paymentMode), // "UPI" | "CASH"
        paymentStatus: paymentPaid ? "paid" : "unpaid",
      };
      const res = await axios.post(
        `${baseUrl}${API_UPDATE_PAYMENT}`,
        payload,
        config
      );

      if (res?.data?.responseCode === "00") {
        toast.success(
          res?.data?.responseMessage ||
            "Payment status updated"
        );
        // Optimistically update table
        setRows((prev) =>
          prev.map((r) =>
            r.id === payRow.id
              ? { ...r, status: payload.paymentStatus }
              : r
          )
        );
        setPayOpen(false);
      } else {
        toast.error(
          res?.data?.responseMessage ||
            "Failed to update payment"
        );
      }
    } catch {
      toast.error("API error while updating payment");
    } finally {
      setPaySubmitting(false);
    }
  };

  return (
    <div className="admin-credit-block">
      {/* Scoped button styles for rounded/green/hover */}
      <style>{`
        .btn-pill {
          padding: 6px 12px;
          font-size: 12px;
          line-height: 1.15;
          height: 30px;
          border-radius: 9999px;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          border: 1px solid transparent;
          box-shadow: 0 1px 1px rgba(0,0,0,.04);
          transition: transform .12s ease, box-shadow .12s ease, background-color .12s ease, color .12s ease, border-color .12s ease;
          cursor: pointer;
          user-select: none;
          white-space: nowrap;
        }
        .btn-pill:hover { transform: translateY(-0.5px); box-shadow: 0 2px 6px rgba(0,0,0,.08); }
        .btn-pill:active { transform: translateY(0); box-shadow: 0 1px 2px rgba(0,0,0,.12) inset; }

        .btn-success { background:#01C37D; color:#fff; }
        .btn-success:hover { background:#00b06f; }
        .btn-success:focus { outline: 2px solid rgba(1,195,125,.25); }

        .btn-neutral { background:#F6F7FB; color:#111827; border-color:#E5E7EB; }
        .btn-neutral:hover { background:#eef1f6; }

        .btn-primary { background:#2563eb; color:#fff; }
        .btn-primary:hover { background:#1d4ed8; }

        .btn-outline { background:#fff; color:#374151; border-color:#E5E7EB; }
        .btn-outline:hover { background:#f9fafb; }

        .btn-danger {
          background:#fef2f2;
          color:#b91c1c;
          border-color:#fecaca;
        }
        .btn-danger:hover {
          background:#fee2e2;
        }

        .btn-xs { padding: 4px 10px; height: 28px; }
      `}</style>

      <ToastContainer
        position="top-right"
        closeOnClick
        pauseOnHover={false}
      />

      {/* 🔍 Global search on top */}
      <div className="admin-credit-header">
        <div>
          <h3 className="admin-credit-title">Order Invoices</h3>
          <p className="admin-credit-subtitle">
            View, download, update payment status, and manage invoices
          </p>
        </div>
        <div
          className="admin-credit-actions"
          style={{ gap: 8 }}
        >
          <div className="admin-input with-icon">
            <Search
              size={16}
              className="admin-input-icon"
            />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by invoice no, mobile, name, status…"
            />
          </div>
          <button
            className="btn-pill btn-neutral btn-xs"
            onClick={fetchInvoices}
            disabled={loading}
            title="Refresh invoices"
          >
            {loading ? (
              <Loader2 className="admin-spin" size={16} />
            ) : (
              <RefreshCcw size={16} />
            )}
            <span style={{ marginLeft: 6 }}>
              {loading ? "Loading…" : "Refresh"}
            </span>
          </button>
        </div>
      </div>

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th style={{ textAlign: "left" }}>Invoice #</th>
              <th style={{ textAlign: "left" }}>Customer</th>
              <th style={{ textAlign: "left" }}>Mobile</th>
              <th className="right" style={{ textAlign: "right" }}>
                Amount
              </th>
              <th style={{ textAlign: "center" }}>Status</th>
              <th style={{ textAlign: "left" }}>Created On</th>
              <th style={{ textAlign: "center", width: 160 }}>
                Payment
              </th>
              <th
                style={{ width: 260, textAlign: "right" }}
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8} className="center">
                  Loading…
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={8} className="center muted">
                  No invoices found
                </td>
              </tr>
            ) : (
              filtered.map((r) => (
                <tr key={r.id}>
                  <td
                    className="mono"
                    style={{ textAlign: "left" }}
                  >
                    {r.number || "—"}
                  </td>
                  <td style={{ textAlign: "left" }}>
                    {r.name || "—"}
                  </td>
                  <td
                    className="mono"
                    style={{ textAlign: "left" }}
                  >
                    {r.mobile || "—"}
                  </td>
                  <td
                    className="mono"
                    style={{ textAlign: "right" }}
                  >
                    {toCurrency(r.amount)}
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <span
                      className={`admin-badge ${badgeClass(
                        r.status
                      )}`}
                    >
                      {String(r.status || "").toUpperCase()}
                    </span>
                  </td>
                  <td style={{ textAlign: "left" }}>
                    {r.created_on || "—"}
                  </td>

                  {/* Payment column: green pill */}
                  <td style={{ textAlign: "center" }}>
                    <button
                      className="btn-pill btn-success btn-xs"
                      onClick={() => openPayment(r)}
                      title="Update payment mode/status"
                    >
                      <CreditCard size={14} />
                      Payment
                    </button>
                  </td>

                  {/* Actions: rounded pills */}
                  <td style={{ textAlign: "right" }}>
                    <div
                      style={{
                        display: "inline-flex",
                        gap: 8,
                        flexWrap: "wrap",
                        justifyContent: "flex-end",
                      }}
                    >
                      <button
                        className="btn-pill btn-outline btn-xs"
                        onClick={() => viewInvoice(r.link)}
                        title="Open in new tab"
                      >
                        <ExternalLink size={14} />
                        View
                      </button>
                      {/* <button
                        className="btn-pill btn-primary btn-xs"
                        onClick={() => downloadInvoice(r)}
                        title="Download image"
                      >
                        <Download size={14} />
                        Download
                      </button> */}
                      <button
                        className="btn-pill btn-primary btn-xs"
                        onClick={() => copyLink(r.link)}
                        title="Copy invoice link"
                      >
                        <Copy size={14} />
                        Copy
                      </button>
                      {r.status !=="paid" ? <button
                        className="btn-pill btn-danger btn-xs"
                        onClick={() => deleteInvoice(r)}
                        title="Delete invoice"
                      >
                        <Trash2 size={14} />
                        Delete
                      </button>:""}
                     
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Payment Update Modal */}
      {payOpen && (
        <div
          className="admin-modal-backdrop"
          onClick={closePayment}
        >
          <div
            className="admin-modal"
            onClick={(e) => e.stopPropagation()}
            style={{ width: "95%", maxWidth: 560 }}
          >
            <div className="admin-modal-header">
              <h4
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <CreditCard size={18} />
                Update Payment
              </h4>
              <button
                className="admin-icon-btn"
                onClick={closePayment}
              >
                <X size={18} />
              </button>
            </div>

            <div
              className="admin-drawer-body admin-drawer-form"
              style={{ gap: 14 }}
            >
              <div className="admin-field">
                <label>Invoice</label>
                <div
                  className="admin-input-control mono"
                  style={{
                    display: "flex",
                    gap: 8,
                    alignItems: "center",
                  }}
                >
                  <span>{payRow?.number || "—"}</span>
                  <span
                    className="muted"
                    style={{
                      marginLeft: "auto",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <IndianRupee size={14} />
                    {toCurrency(payRow?.amount || "0")}
                  </span>
                </div>
              </div>

              <div className="admin-field">
                <label>Payment Mode</label>
                <select
                  className="admin-input-control"
                  value={paymentMode}
                  onChange={(e) =>
                    setPaymentMode(e.target.value)
                  }
                >
                  <option value="UPI">UPI</option>
                  <option value="CASH">CASH</option>
                </select>
                <div
                  className="muted"
                  style={{
                    fontSize: 12,
                    marginTop: 6,
                  }}
                >
                  Default is <b>UPI</b>. Change to{" "}
                  <b>CASH</b> if paid in cash.
                </div>
              </div>

              <div className="admin-field">
                <label>Payment Status</label>
                <div
                  className="admin-toggle"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "10px 12px",
                    border: "1px solid #e5e7eb",
                    borderRadius: 9999,
                    background: "#fafafa",
                  }}
                >
                  <input
                    id="payStatus"
                    type="checkbox"
                    checked={paymentPaid}
                    onChange={(e) =>
                      setPaymentPaid(e.target.checked)
                    }
                  />
                  <label
                    htmlFor="payStatus"
                    style={{
                      margin: 0,
                      cursor: "pointer",
                    }}
                  >
                    Mark as{" "}
                    <b>{paymentPaid ? "Paid" : "Unpaid"}</b>
                  </label>
                </div>
              </div>
            </div>

            <div
              className="admin-modal-actions"
              style={{ justifyContent: "space-between" }}
            >
              <button
                className="btn-pill btn-outline btn-xs"
                onClick={closePayment}
                disabled={paySubmitting}
              >
                Cancel
              </button>
              <button
                className="btn-pill btn-success btn-xs"
                onClick={submitPaymentUpdate}
                disabled={paySubmitting}
                title="Save payment mode & status"
              >
                {paySubmitting ? (
                  <Loader2
                    className="admin-spin"
                    size={16}
                  />
                ) : (
                  <Check size={16} />
                )}
                <span style={{ marginLeft: 6 }}>
                  {paySubmitting ? "Updating…" : "Update Payment"}
                </span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
